/**
 * BlastRadiusPanel — pre-destructive guard modal for VqeDdlEdit.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice H3.
 *
 * The Visual DDL Editor's `commitDdlEdit` pipeline stages a
 * `VqeDdlPendingBlastRadius` in `vqeDdlStore` whenever the edit is a drop op
 * AND the host's `DependencyAnnotations` snapshot reports at least one
 * incoming dependent. This dialog is the global mount point that surfaces
 * the dependent list and gates commit on user confirmation.
 *
 * Two outcomes:
 *   - **Confirm** — invokes the staged `proceed()` thunk (re-enters the
 *     commit pipeline with the gate stripped) and clears the staged state.
 *   - **Cancel** — clears the staged state without invoking `proceed()`. The
 *     buffer remains exactly as it was before the gate.
 *
 * Each dependent row carries a "Jump to" link that opens the dependent in a
 * Dependency Viewer tab — a finer-grained hand-off than the panel-level
 * "Open in Dependency Viewer" button.
 */
import { useCallback, useState } from 'react';
import { AlertTriangle, ExternalLink, Eye, Code, Zap, Database, Table2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { DependencyItem, DependencyObjectType } from '../../dependencies/types';
import { useVqeDdlStore } from '../../store';

export const BLAST_RADIUS_PANEL_TESTID = 'blast-radius-panel';
export const BLAST_RADIUS_CONFIRM_TESTID = 'blast-radius-panel-confirm';
export const BLAST_RADIUS_CANCEL_TESTID = 'blast-radius-panel-cancel';
export const BLAST_RADIUS_JUMP_TESTID = 'blast-radius-panel-jump';

export interface BlastRadiusPanelProps {
  /** Optional jump-to handler — when supplied, each dependent row exposes
   *  a "Jump to" link that opens the dependent in a Dependency Viewer tab.
   *  The default mounted-at-app-shell instance wires this to the workspace
   *  store. */
  readonly onJumpToDependent?: (item: DependencyItem) => void;
}

export function BlastRadiusPanel({ onJumpToDependent }: BlastRadiusPanelProps = {}) {
  const pending = useVqeDdlStore((s) => s.pendingBlastRadius);
  const commitBlastRadius = useVqeDdlStore((s) => s.commitBlastRadius);
  const cancelBlastRadius = useVqeDdlStore((s) => s.cancelBlastRadius);
  const { track } = useAnalytics();
  const [proceeding, setProceeding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = useCallback(async () => {
    if (!pending || proceeding) return;
    setProceeding(true);
    setError(null);
    try {
      await pending.proceed();
      track('feature:vqe_ddl_blast_radius_decision', {
        decision: 'proceed',
        op: pending.edit.op,
        source: pending.source,
        dependentCount: pending.dependents.length,
        unverified: pending.dependentsUnverified === true,
      });
      commitBlastRadius();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setProceeding(false);
    }
  }, [pending, proceeding, track, commitBlastRadius]);

  const handleCancel = useCallback(() => {
    if (!pending || proceeding) return;
    track('feature:vqe_ddl_blast_radius_decision', {
      decision: 'cancel',
      op: pending.edit.op,
      source: pending.source,
      dependentCount: pending.dependents.length,
      unverified: pending.dependentsUnverified === true,
    });
    cancelBlastRadius();
    setError(null);
  }, [pending, proceeding, track, cancelBlastRadius]);

  const open = pending !== null;
  // VDE-9 fail-safe: dependency annotations were not a confirmed success, so
  // the dependent list is UNKNOWN — never claim a count or "none recorded".
  const unverified = pending?.dependentsUnverified === true;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
    >
      <DialogContent
        className="max-w-2xl"
        data-testid={BLAST_RADIUS_PANEL_TESTID}
        data-dependent-count={pending?.dependents.length ?? 0}
        data-dependents-unverified={unverified ? 'true' : undefined}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            Confirm destructive change
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">
              {pending?.target.actionLabel}
              {pending?.target.targetLabel ? ` — ${pending.target.targetLabel}` : ''}
            </span>{' '}
            on{' '}
            <span className="font-mono">
              {pending?.target.schema ? `${pending.target.schema}.` : ''}
              {pending?.target.objectName ?? '(unnamed)'}
            </span>{' '}
            {unverified ? (
              <>
                — dependents <strong>could not be verified</strong>.
              </>
            ) : (
              <>
                will affect {pending?.dependents.length ?? 0} dependent
                {pending && pending.dependents.length === 1 ? '' : 's'}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="max-h-[40vh] overflow-auto rounded-md border border-border bg-muted/40 p-2 text-xs"
          data-testid="blast-radius-panel-list"
        >
          {unverified ? (
            <p className="italic text-muted-foreground">
              Dependents could not be verified — the dependency lookup did not
              complete. Proceeding may break dependent objects.
            </p>
          ) : pending && pending.dependents.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {pending.dependents.map((item, idx) => (
                <BlastRadiusRow
                  key={`${item.schema}-${item.name}-${idx}`}
                  item={item}
                  onJump={onJumpToDependent}
                />
              ))}
            </ul>
          ) : (
            <p className="italic text-muted-foreground">No dependents recorded.</p>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground">
          This list comes from the database catalog. Application code may also
          depend on this object — review before proceeding.
        </p>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
            <span data-testid="blast-radius-panel-error">{error}</span>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={proceeding}
            data-testid={BLAST_RADIUS_CANCEL_TESTID}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={proceeding}
            data-testid={BLAST_RADIUS_CONFIRM_TESTID}
          >
            {proceeding ? 'Applying…' : 'Confirm and proceed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BlastRadiusRowProps {
  readonly item: DependencyItem;
  readonly onJump?: (item: DependencyItem) => void;
}

function BlastRadiusRow({ item, onJump }: BlastRadiusRowProps) {
  const fullName = item.schema ? `${item.schema}.${item.name}` : item.name;
  return (
    <li className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted">
      <span className="flex-shrink-0">{iconForType(item.objectType)}</span>
      <span className="flex-1 truncate font-mono text-[11px]">{fullName}</span>
      <span className="text-[10px] uppercase text-muted-foreground">
        {item.objectType}
      </span>
      {onJump ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-background hover:text-foreground"
          onClick={() => onJump(item)}
          data-testid={BLAST_RADIUS_JUMP_TESTID}
        >
          <ExternalLink className="h-3 w-3" />
          Jump to
        </button>
      ) : null}
    </li>
  );
}

function iconForType(type: DependencyObjectType) {
  switch (type) {
    case 'table':
      return <Table2 className="h-3 w-3 text-blue-500" />;
    case 'view':
      return <Eye className="h-3 w-3 text-green-500" />;
    case 'procedure':
    case 'function':
      return <Code className="h-3 w-3 text-orange-500" />;
    case 'trigger':
      return <Zap className="h-3 w-3 text-yellow-500" />;
    default:
      return <Database className="h-3 w-3 text-muted-foreground" />;
  }
}
